import { Api } from '@/Api';
import { defineStore } from 'pinia';

export interface Notification {
  id: string;
  title?: string;
  body?: string;
  actionLink?: string;
  actionText?: string;
  createdAt: Date;
  dismissText?: string;
  imageUrl?: string;
  messageType?: null; // ??

  viewedAt?: Date;
}

export interface NotificationState {
  items: Notification[];
  unreadCount: number;
  isLoadedAll: boolean;
}

export const useNotificationsStore = defineStore('notificationsStore', {
  state: () =>
    ({
      items: [],
      unreadCount: 0,
      isLoadedAll: false,
    }) as NotificationState,
  actions: {
    initialize(data: any, clear: boolean = false) {
      if (this.items.length === data.total || !data.total) {
        return this.$patch({ isLoadedAll: true });
      }

      if (!data.items) {
        return;
      }

      const formattedData = data.items.map((item: any) => ({
        id: item?.id,
        ...item.attributes,
        createdAt: new Date(item.attributes.createdAt),
        viewedAt: item.attributes.viewedAt ? new Date(item.attributes.viewedAt) : undefined,
      }));

      if (data.unreadCount !== undefined) {
        this.$patch({ unreadCount: data.unreadCount });
      }

      if (clear) {
        this.$patch({ items: formattedData });
      } else {
        this.$patch({ items: this.items.concat(formattedData) });
      }
    },
    load() {
      Api.notifications.get().then((res: any) => this.initialize(res, true));
    },
    loadMore(page: number = 1, limit: number = 10, force: boolean = false) {
      if (!force && this.isLoadedAll) {
        return Promise.resolve();
      }

      return Api.notifications.get(page, limit).then((res: any) => this.initialize(res));
    },
    markAsRead(id: string) {
      return Api.notifications.markAsRead(id).then(() => {
        this.$patch(state => {
          const idx = state.items.findIndex(item => item.id === id);
          state.items[idx].viewedAt = new Date();
        });
      });
    },
    markAllAsRead() {
      const last = this.items.at(0);

      if (!last || !last.createdAt) {
        return Promise.resolve();
      }

      if (!this.hasUnread) {
        return Promise.resolve();
      }

      return Api.notifications.markAllAsRead(last.createdAt).then(() => this.load());
    },
  },
  getters: {
    hasUnread: (state: NotificationState) => state.unreadCount > 0,
  },
});
